/**
 * Rendert einen Segment-Notiz-Body (#32): Freitext mit @-Mentions UND inline eingefügten
 * Bildern (`![alt](url)`, GitHub-Stil, PV-Feedback 2026-07).
 *
 * Bilder werden NUR gerendert, wenn ihre URL auf den eigenen Bild-Endpunkt zeigt
 * (relativer Pfad, der `/v1/segment-notes/images/` enthält). Extern verlinkte Bilder
 * (`![](http://…)`) bleiben bewusst roher Text — kein Laden fremder Hosts aus Notizen.
 */
import type { User } from '@projektverwaltung/shared';
import type React from 'react';
import { renderMentionBody } from '../mention-input.js';

export type NoteSegment =
  | { type: 'text'; value: string }
  | { type: 'image'; src: string; alt: string };

// Markdown-Bild: ![alt](url) — url ohne Leerzeichen/schließende Klammer.
const IMAGE_TOKEN = /!\[([^\]]*)\]\(([^)\s]+)\)/g;

/**
 * Nur eigene, relative Bild-URLs sind renderbar. Harter Präfix-Check statt `includes`:
 * Backslashes werden von Browsern in URLs zu `/` normalisiert (`/\host` → `//host`,
 * scheme-relativ → externer Host), deshalb explizit verbieten. So kann eine Notiz keinen
 * Bild-Load von fremden Hosts (Tracking-Pixel) auslösen. Der eigene Uploader liefert stets
 * `${BASE}/v1/segment-notes/images/<id>` mit BASE `/api` (relativer Prod-Default).
 */
export function isSafeNoteImageUrl(url: string): boolean {
  if (url.includes('\\')) return false;
  return (
    url.startsWith('/v1/segment-notes/images/') || url.startsWith('/api/v1/segment-notes/images/')
  );
}

/**
 * Zerlegt den Body in Text- und Bild-Segmente. Unsichere Bild-URLs bleiben Teil des
 * umgebenden Textes (werden also nicht als `<img>` gerendert). Rein — unit-getestet.
 */
export function splitNoteBody(body: string): NoteSegment[] {
  const segments: NoteSegment[] = [];
  let last = 0;
  IMAGE_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: idiomatische Regex-Iteration
  while ((m = IMAGE_TOKEN.exec(body)) !== null) {
    const full = m[0];
    const alt = m[1] ?? '';
    const url = m[2] ?? '';
    if (!isSafeNoteImageUrl(url)) continue; // bleibt roher Text (kein Segment-Schnitt hier)
    if (m.index > last) segments.push({ type: 'text', value: body.slice(last, m.index) });
    segments.push({ type: 'image', src: url, alt });
    last = m.index + full.length;
  }
  if (last < body.length) segments.push({ type: 'text', value: body.slice(last) });
  return segments;
}

/**
 * Rendert den Body: Text-Segmente mit Mentions, Bild-Segmente als `<img>`.
 * Die Segmentliste ist deterministisch aus `body` abgeleitet und wird nie umsortiert,
 * daher ist der Positions-Key stabil (Key in Variable → kein noArrayIndexKey-Fehlalarm).
 */
export function renderNoteBody(body: string, usersById: Map<string, User>): React.ReactNode {
  const segments = splitNoteBody(body);
  return segments.map((seg, i) => {
    const key = `${seg.type}-${i}`;
    if (seg.type === 'image') {
      return (
        <img
          key={key}
          src={seg.src}
          alt={seg.alt || 'Screenshot'}
          className="max-w-full rounded-md my-1.5 block"
          style={{ maxHeight: 420, border: '1px solid var(--line-soft)' }}
        />
      );
    }
    return <span key={key}>{renderMentionBody(seg.value, usersById)}</span>;
  });
}
